import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import {
  EditarContasAPagarFormData,
  editarContasAPagarSchema,
} from "./schema/editar-contas-a-pagar.schema";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { contasAPagarService } from "./service/criar-contas-a-pagar.service";
import { toastError } from "../../shared/services/errors/erros-handler";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import InputSetores from "../../shared/components/formulario/inputSetores/inputSetor";
import InputPlanoDeContas from "../../shared/components/formulario/inputPlanoDeContas/inputPlanoDeContas";
import { MovementTypeEnum } from "../financeiro/schema/gerar-movimentacao-obra.schema";
import { SelectNfsEmpresa } from "../../shared/components/formulario/input-select-nfs/selectNfs";
import { useEffect, useState } from "react";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import {
  MovementOriginEnum,
  PeriodicityEnum,
} from "../../shared/utils/enum/enum";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { useSectorById } from "../../shared/components/formulario/inputSetores/use-sector";
import { formatDate } from "../../shared/utils";
import { useMutation } from "@tanstack/react-query";
import { SelectNfsCompras } from "../../shared/components/formulario/input-select-nfs-compra/selectNfsCompra";
import { SelectFornecedores } from "../../shared/components/formulario/input-select-fornecedores/selectFornecedores";
import { InputFileUpload } from "../../shared/components/formulario/input-file/inputFile";

export function EditarContasPagar() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const methods = useForm<EditarContasAPagarFormData>({
    resolver: yupResolver(editarContasAPagarSchema),
    defaultValues: {},
  });

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
    watch,
  } = methods;

  const [notaTipo, setNotaTipo] = useState<"nenhuma" | "servico" | "compra">(
    "nenhuma"
  );

  const [data, setData] = useState<EditarContasAPagarFormData>({} as any);

  const antecipateAccount = watch("anticipateAccount");

  // Carregamento dos dados da conta
  useEffect(() => {
    if (id) {
      contasAPagarService
        .detalhar(id)
        .then((conta) => {
          setData(conta);
          reset({
            ...conta,
            idSector: conta.idSector ?? "",
            chartOfAccountsId: conta.chartOfAccountsId ?? "",
            description: conta.description ?? "",
            totalValue: Number(conta.totalValue) ?? 0,
            purchaseDate: conta.purchaseDate
              ? formatDate(conta.purchaseDate)
              : "",
            numberOfInstallments: conta.numberOfInstallments ?? 1,
            clientId: conta.clientId ?? "",
            workId: conta.workId ?? "",
            periodicity: conta.periodicity ?? "",
            idNfs: conta.idNfs ?? "",
            idInvoicePurchase: conta.idInvoicePurchase ?? "",
            supplierId: conta.supplierId ?? "",
            ticket: conta.ticket ?? "",
            anticipateAccount: !!conta.anticipateAccount,
            newTotalValue: conta.newTotalValue
              ? Number(conta.newTotalValue)
              : undefined,
          });
        })
        .catch((error) => {
          toastError(error);
        });
    }
    // eslint-disable-next-line
  }, [id, reset]);

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: EditarContasAPagarFormData) => {
      const payload = {
        ...data,
        clientId: !data.clientId ? null : data.clientId,
        workId: !data.workId ? null : data.workId,
        idNfs: !data.idNfs ? null : data.idNfs,
      };
      const result = await contasAPagarService.editar(id || "", payload as any);
      return result;
    },
    onSuccess: () => {
      toast.success("Conta editada com sucesso!");
      navigate("/financeiro/contas-a-pagar");
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  // Watchers
  const sector = watch("idSector");
  const idCliente = watch("clientId");
  const idObra = watch("workId");
  const setorSelecionado = useSectorById(sector);
  const idFornecedor = watch("supplierId");

  useEffect(() => {
    if (data.idNfs != null) {
      setNotaTipo("servico");
    } else if (data.idInvoicePurchase != null) {
      setNotaTipo("compra");
    } else {
      setNotaTipo("nenhuma");
    }
  }, [data]);

  useEffect(() => {
    if (notaTipo !== "servico") {
      setValue("idNfs" as keyof EditarContasAPagarFormData, null);
    }
    if (notaTipo !== "compra") {
      setValue("idInvoicePurchase" as keyof EditarContasAPagarFormData, null);
      setValue("supplierId" as keyof EditarContasAPagarFormData, null);
    }
  }, [notaTipo, setValue]);

  return (
    <BaseLayout title="Editar Conta a Pagar">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => mutate(formData))}
        aoClicarBotaoVoltar={() => navigate("/financeiro/contas-a-pagar")}
      />
      <Box
        component="form"
        onSubmit={handleSubmit((formData) => mutate(formData))}
        padding={isMobile ? "8px 0" : isTablet ? "16px 0" : "20px 0"}
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputSetores
              name="idSector"
              label="Setor de Origem"
              methods={methods}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputPlanoDeContas
              name="chartOfAccountsId"
              label="Plano de Contas"
              tiPosMovimentacao={MovementTypeEnum.OUTCOME || ""}
              methods={methods}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição"
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputMascarado
              name="totalValue"
              methods={methods}
              label="Valor Total"
              mask={mascaraMoedaReal}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputData
              name="purchaseDate"
              methods={methods}
              label="Data de Vencimento"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="numberOfInstallments"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Quantidade de Parcelas"
                  type="number"
                  error={!!errors.numberOfInstallments}
                  helperText={errors.numberOfInstallments?.message}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="periodicity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Periodicidade"
                  error={!!errors.periodicity}
                  helperText={errors.periodicity?.message}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Selecione</MenuItem>
                  <MenuItem value={PeriodicityEnum.MONTHLY}>Mensal</MenuItem>
                  <MenuItem value={PeriodicityEnum.BIWEEKLY}>
                    Bimestral
                  </MenuItem>
                  <MenuItem value={PeriodicityEnum.WEEKLY}>Semestral</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>

        {setorSelecionado?.origin === MovementOriginEnum.WORK && (
          <Grid container spacing={2} mt={isMobile ? 2 : 3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="clientId"
                control={control}
                render={({ field }) => (
                  <SelectCliente
                    value={field.value || ""}
                    onChange={(id) => field.onChange(id)}
                    required={true}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="workId"
                control={control}
                render={({ field }) => (
                  <SelectObra
                    value={field.value || ""}
                    onChange={(id) => field.onChange(id)}
                    idCliente={idCliente || ""}
                    required={true}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2} mt={isMobile ? 2 : 3}>
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">
                Relacionar a uma nota fiscal?
              </FormLabel>
              <RadioGroup
                row={!isMobile}
                value={notaTipo}
                onChange={(e) => setNotaTipo(e.target.value as any)}
              >
                <FormControlLabel
                  value="nenhuma"
                  control={<Radio />}
                  label="Nenhuma"
                />
                <FormControlLabel
                  value="servico"
                  control={<Radio />}
                  label="Nota Fiscal de Serviço"
                />
                <FormControlLabel
                  value="compra"
                  control={<Radio />}
                  label="Nota Fiscal de Compra"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {notaTipo === "servico" && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="idNfs"
                control={control}
                render={({ field }) => (
                  <SelectNfsEmpresa
                    name="idNfs"
                    methods={methods}
                    idWork={idObra || ""}
                    label="Selecione uma Nota Fiscal de Serviço"
                    disabled={notaTipo !== "servico"}
                  />
                )}
              />
            </Grid>
          )}
          {notaTipo === "compra" && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="supplierId"
                  control={control}
                  render={({ field }) => (
                    <SelectFornecedores
                      value={field.value || ""}
                      onChange={field.onChange}
                      disabled={notaTipo !== "compra"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="idInvoicePurchase"
                  control={control}
                  render={({ field }) => (
                    <SelectNfsCompras
                      name="idInvoicePurchase"
                      methods={methods}
                      workId={idObra || ""}
                      supplierId={idFornecedor || ""}
                      label="Selecione uma Nota Fiscal de Compra"
                      disabled={notaTipo !== "compra"}
                    />
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Grid container spacing={2} mt={isMobile ? 2 : 3}>
          <Grid item xs={12}>
            <Controller
              name="anticipateAccount"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Antecipar Conta"
                />
              )}
            />
          </Grid>

          {antecipateAccount && (
            <>
              <Grid item xs={12} sm={6}>
                <InputData
                  name="newDateDoc"
                  methods={methods}
                  label="Nova Data do Documento"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputMascarado
                  name="newTotalValue"
                  methods={methods}
                  label="Novo Valor Total"
                  mask={mascaraMoedaReal}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box width="100%" padding="20px 0">
          <InputFileUpload
            name="ticket"
            label="Faça o upload dos boletos, se desejar, nos formatos PDF, JPG ou PNG."
            methods={methods}
            required={false}
          />
        </Box>
      </Box>
    </BaseLayout>
  );
}
